/**
 * Bilingual, hand-written content for the Advanced Local SEO landing pages.
 * Real content only — no fake reviews, no fake statistics, no keyword stuffing.
 * Brand truth: Yosi Media Studio, Huntsville, AL. Phone +1 (256) 288-8270.
 * Email info@yosimediastudio.com.
 *
 * Every entry provides:
 *   slugEn / slugEs          — URL slug per language
 *   title / description      — SEO title & meta description
 *   h1                       — main heading
 *   intro                    — first paragraph
 *   whyLocalTitle / bullets  — "Why Huntsville businesses choose us" section
 *   whatIncludedTitle / list — deliverables (real, no fluff)
 *   processTitle / steps     — our workflow
 *   areasTitle / areas       — real neighboring cities/areas we serve
 *   faqTitle / faqs          — 4-6 real, high-intent FAQs
 *   ctaTitle / ctaBody       — closing block
 *   serviceType              — Schema.org Service.serviceType
 *   priceHint                — human-readable pricing hint (no fake ranges)
 */

const AREAS_EN = [
  { name: "Huntsville, AL", note: "Our hometown — same-day meetings available." },
  { name: "Madison, AL", note: "Growing tech corridor, we work with SMBs along Hughes Rd." },
  { name: "Decatur, AL", note: "Manufacturing and services clients across the Tennessee Valley." },
  { name: "Athens, AL", note: "Small-business focused websites and local search." },
  { name: "Hampton Cove & Owens Cross Roads", note: "Boutique retailers, clinics and trades." },
  { name: "Meridianville & Hazel Green", note: "Northern Madison County service businesses." },
];

const AREAS_ES = [
  { name: "Huntsville, AL", note: "Nuestra ciudad — reuniones el mismo día disponibles." },
  { name: "Madison, AL", note: "Corredor tecnológico en crecimiento; atendemos comercios sobre Hughes Rd." },
  { name: "Decatur, AL", note: "Clientes de manufactura y servicios en el Valle del Tennessee." },
  { name: "Athens, AL", note: "Sitios web y posicionamiento local para pequeños negocios." },
  { name: "Hampton Cove y Owens Cross Roads", note: "Comercios boutique, clínicas y oficios." },
  { name: "Meridianville y Hazel Green", note: "Negocios de servicio del norte del condado de Madison." },
];

export const LOCAL_SEO_PAGES = {
  "web-design": {
    en: {
      slug: "web-design-huntsville",
      title: "Web Design Huntsville AL | Yosi Media Studio",
      description:
        "Web Design Huntsville AL — custom, mobile-responsive, SEO-friendly websites for Huntsville businesses and contractors. Serving Huntsville, Madison, Decatur & Athens. Call +1 (256) 288-8270 for a free quote.",
      h1: "Web Design Huntsville AL",
      intro:
        "Yosi Media Studio is a Huntsville web design studio building custom, mobile-responsive websites for local businesses across Huntsville, Madison, Decatur, Athens and the wider North Alabama region. Every site is engineered to help Huntsville customers find you on Google, generate qualified leads, and turn visitors into paying customers — no templates, no lock-in, and every SEO foundation Google looks for is in place from day one.",
      whyLocalTitle: "Why Huntsville Businesses Choose Our Web Design",
      bullets: [
        "Huntsville custom web design — every layout hand-built around your brand and your buyers, never a template kit.",
        "Mobile-responsive design tuned for real Alabama LTE speeds, not just Chrome DevTools.",
        "SEO-friendly websites out of the box: clean HTML, correct heading hierarchy, Schema.org, XML sitemap.",
        "Bilingual English & Spanish web design when your Huntsville customer base speaks both.",
        "Lead-generation ready: contact forms, click-to-call, quote requests and WhatsApp wired into your CRM.",
        "Local web designer you can meet in person across Madison County when it matters.",
      ],
      whatIncludedTitle: "Custom Website Design Built to Generate Leads",
      list: [
        "Huntsville custom website design — hand-coded, not page-builder bloat.",
        "Fully mobile-responsive design tested on real iPhones and Androids, not simulators.",
        "SEO-friendly structure: H1–H3 hierarchy, meta tags, image alt text, Schema.org, XML sitemap.",
        "Lead-generation stack: contact forms, click-to-call buttons, quote requests, WhatsApp.",
        "Local SEO integration aligned with your Google Business Profile and Huntsville search intent.",
        "Call-tracking-ready markup so every Huntsville phone lead can be attributed to the channel that drove it.",
        "Conversion-focused layouts with clear CTAs on every scroll fold.",
        "Analytics + conversion tracking installed and verified before launch.",
      ],
      processTitle: "Our Huntsville Web Design Process",
      steps: [
        { t: "Week 1 — Discovery", d: "Business goals, target Huntsville customers, competitor review, sitemap agreed." },
        { t: "Weeks 2–3 — Design", d: "Custom homepage + internal templates in your brand system with weekly reviews." },
        { t: "Weeks 4–6 — Development", d: "Hand-coded build in staging with weekly demos and copy iteration." },
        { t: "Week 7 — Launch", d: "QA on real devices, analytics + tracking live, DNS cutover, handoff docs." },
      ],
      industriesTitle: "Website Design for Huntsville Contractors and Local Businesses",
      industriesIntro:
        "We specialise in website design for contractors in Huntsville and lead-driven local businesses across North Alabama — the kind of companies whose next month depends on the calls that come in this week. Whether you're a contractor, tree service, medical clinic, or home-services company in Huntsville, Madison, Decatur or Athens, we build a website that pulls its weight.",
      industries: [
        "Contractors",
        "Construction Companies",
        "Tree Services",
        "Medical Clinics",
        "Home Services",
        "Local Businesses",
      ],
      internalLinksTitle: "Pair Your Huntsville Website With The Rest Of The Growth Stack",
      internalLinksIntro:
        "A great website is the foundation — real lead flow in Huntsville comes when it's connected to the local SEO, digital marketing and tracking systems that drive and measure demand.",
      internalLinks: [
        { title: "SEO Huntsville", desc: "Rank higher for Huntsville searches and win the local map pack.", to: "/services/seo-huntsville" },
        { title: "Digital Marketing Huntsville", desc: "Google Ads, Meta ads and landing pages that turn Huntsville clicks into calls.", to: "/services/digital-marketing-huntsville" },
        { title: "Google Business Profile", desc: "Own the Huntsville local map with an actively-managed GBP.", to: "/services/gbp" },
        { title: "Call Tracking", desc: "Attribute every Huntsville phone lead to the right channel.", to: "/services/call-tracking" },
        { title: "AI Automation", desc: "Qualify and follow up on leads 24/7 — English and Spanish.", to: "/services/ai-automation-huntsville" },
        { title: "Free Growth Audit", desc: "We'll review your current site and lead flow in writing.", to: "/contact?audit=1" },
      ],
      areasTitle: "Serving Huntsville and surrounding cities",
      areas: AREAS_EN,
      faqTitle: "Frequently asked questions",
      faqs: [
        {
          q: "How much does a business website cost in Huntsville?",
          a: "Every project is quoted at a fixed price after a free discovery call — no hourly surprises. Costs vary by scope: a focused 5–7 page lead-generation site sits well below a fully custom multi-language marketing platform. Call +1 (256) 288-8270 for a same-week written quote.",
        },
        {
          q: "How long does it take to build a website?",
          a: "A focused business website with 5–7 pages typically launches in 4–6 weeks once content is agreed. Larger builds with e-commerce, custom integrations, or bilingual content usually ship in 8–12 weeks.",
        },
        {
          q: "Do you provide SEO with website development?",
          a: "Yes. Every site we ship includes on-page SEO foundations — clean HTML, proper heading hierarchy, Schema.org markup, XML sitemap, meta tags, image alt text, and page-speed tuning. Ongoing ranking work is available as an optional monthly Local SEO retainer.",
        },
        {
          q: "Can you redesign an existing website?",
          a: "Yes — website redesigns are one of the most common projects we take on. We audit what's already working, preserve the SEO equity that matters, and rebuild the rest around a design system built for lead generation.",
        },
        {
          q: "Do you work with small businesses in Huntsville?",
          a: "Yes. Most of our clients are small and mid-sized businesses across Huntsville, Madison, Decatur, and Athens. We offer fixed-scope quotes so budgets stay predictable and every deliverable is defined up front.",
        },
        {
          q: "Can the website generate calls and leads?",
          a: "That's the entire point of how we design. Every site we ship includes clear calls-to-action, click-to-call, contact forms, and call-tracking-ready markup. When it's paired with Local SEO and Google Business Profile work, the same site keeps producing leads month after month.",
        },
      ],
      ctaTitle: "Ready to plan your new website?",
      ctaBody:
        "Call +1 (256) 288-8270 or email info@yosimediastudio.com and we'll set up a free 30-minute discovery call this week — no obligation.",
      serviceType: "Web Design",
      priceHint: "Fixed-scope quotes issued after a free discovery call.",
    },
    es: {
      slug: "diseno-web-huntsville",
      title: "Diseño Web en Huntsville, AL | Yosi Media Studio",
      description:
        "Diseño de sitios web a medida para negocios en Huntsville, Madison y Decatur. Rápidos, mobile-first y con bases sólidas de SEO. Llame al +1 (256) 288-8270.",
      h1: "Diseño Web en Huntsville, Alabama",
      intro:
        "Diseñamos y desarrollamos sitios web a medida para negocios de Huntsville y del Valle del Tennessee. Cada sitio se construye a mano según su marca, optimizado para Core Web Vitals y con las bases de SEO necesarias para que sus clientes locales lo encuentren.",
      whyLocalTitle: "Por qué los negocios de Huntsville trabajan con nosotros",
      bullets: [
        "Equipo local — nos reunimos en persona en el condado de Madison cuando ayuda.",
        "Servicio bilingüe (inglés y español) si sus clientes hablan ambos idiomas.",
        "Diseño a medida, sin plantillas — cada estructura responde a sus objetivos.",
        "Mobile-first: más de la mitad de las búsquedas locales ocurren en celular.",
        "Rendimiento medido en redes reales de Huntsville, no solo en staging.",
        "Usted es dueño del código y elige el hosting — nada queda amarrado a nosotros.",
      ],
      whatIncludedTitle: "Qué incluye una entrega de diseño web con Yosi",
      list: [
        "Sesión de discovery para alinear objetivos, audiencia y métricas.",
        "Sistema de diseño a medida: tipografía, color, componentes — sin plantillas.",
        "Revisión editorial y SEO estructural (H1–H3, schema, meta, sitemap).",
        "Auditoría de accesibilidad conforme a WCAG 2.1 AA.",
        "Analítica y seguimiento de conversiones activos desde el lanzamiento.",
        "Dos rondas de revisión y una ventana de lanzamiento con soporte.",
      ],
      processTitle: "Cómo funciona el proyecto",
      steps: [
        { t: "Semana 1 — Discovery", d: "Kickoff, deep-dive de marca, sitemap acordado." },
        { t: "Semanas 2–3 — Diseño", d: "Home + plantillas internas, revisiones semanales." },
        { t: "Semanas 4–6 — Desarrollo", d: "Construcción en staging con demos semanales." },
        { t: "Semana 7 — Lanzamiento", d: "QA, cambio de DNS, analítica activa, entrega documentada." },
      ],
      areasTitle: "Atendemos Huntsville y ciudades cercanas",
      areas: AREAS_ES,
      faqTitle: "Preguntas frecuentes",
      faqs: [
        {
          q: "¿Cuánto cuesta un sitio web en Huntsville?",
          a: "La mayoría de nuestros sitios para pequeñas empresas se ubican entre una web informativa clara y una web de marketing totalmente a medida. Cada proyecto se cotiza a precio fijo tras una llamada de discovery — sin cobros por hora. Llame al +1 (256) 288-8270 para una cotización esta misma semana.",
        },
        {
          q: "¿Cuánto tarda mi sitio web?",
          a: "Un sitio de 5 a 7 páginas para una pequeña empresa se entrega en 4–6 semanas una vez definidos los contenidos. Sitios más grandes con e-commerce, integraciones o contenido bilingüe suelen entregarse en 8–12 semanas.",
        },
        {
          q: "¿Ustedes escriben los textos?",
          a: "Sí. Incluimos una revisión editorial en cada proyecto y ofrecemos redacción completa como servicio adicional. Si ya tiene textos, los editamos para SEO y claridad sin costo extra.",
        },
        {
          q: "¿Mi nuevo sitio aparecerá en Google en búsquedas locales?",
          a: "Construimos cada sitio con las bases que Google prioriza: estructura limpia, marcado Schema.org real, jerarquía H1–H3 correcta, rendimiento mobile-first y sitemap real. El posicionamiento también depende de contenido y enlaces continuos — ofrecemos planes mensuales de SEO para mantener el impulso.",
        },
        {
          q: "¿Pueden alojar y mantener el sitio después del lanzamiento?",
          a: "Sí — ofrecemos hosting administrado en datacenter de EE. UU. con monitoreo, respaldos y SLA de soporte el mismo día. También puede alojarlo donde prefiera; en ambos casos entregamos el código fuente completo.",
        },
      ],
      ctaTitle: "¿Listo para planificar su nuevo sitio web?",
      ctaBody:
        "Llame al +1 (256) 288-8270 o escríbanos a info@yosimediastudio.com y coordinamos una llamada gratuita de 30 minutos esta semana.",
      serviceType: "Web Design",
      priceHint: "Cotizaciones a precio fijo tras una llamada de discovery gratuita.",
    },
  },

  "seo": {
    en: {
      slug: "seo-huntsville",
      title: "SEO Services in Huntsville, AL | Local Search Experts",
      description:
        "SEO for Huntsville businesses — technical audits, on-page work, Google Business Profile and local content strategy. Real work, real reporting, no lock-in.",
      h1: "SEO Services in Huntsville, Alabama",
      intro:
        "We help Huntsville businesses win the searches that actually bring customers through the door. Our SEO work is grounded in a technical audit, clear on-page fixes, and a content plan aligned to what people around Madison County are really searching for.",
      whyLocalTitle: "Why local SEO works differently in Huntsville",
      bullets: [
        "Search behaviour here is heavily map-pack driven — Google Business Profile matters.",
        "Reviews and photos from real Huntsville customers carry outsized weight.",
        "Local landing pages must speak to real neighborhoods, not fake city templates.",
        "Growing tech and defense workforce means high-intent, long-tail queries.",
        "Bilingual searchers matter — we can produce Spanish content when it fits.",
      ],
      whatIncludedTitle: "What our SEO engagement covers",
      list: [
        "Technical audit against a 200-point checklist (Core Web Vitals, indexing, schema).",
        "Keyword research grounded in real Huntsville search demand.",
        "On-page optimisation for every existing money page.",
        "Google Business Profile setup or clean-up (categories, posts, Q&A).",
        "Local content plan: neighborhood pages, service pages, blog cadence.",
        "Monthly report you can actually read — no vanity metrics.",
      ],
      processTitle: "Our SEO workflow",
      steps: [
        { t: "Weeks 1–2 — Audit", d: "Technical, content and link audit; competitor mapping." },
        { t: "Week 3 — Strategy", d: "Priority-ranked roadmap with expected effort and impact." },
        { t: "Weeks 4+ — Execute", d: "Technical fixes shipped first; content and GBP in parallel." },
        { t: "Monthly — Report", d: "Rank tracking, traffic, conversions and next-month priorities." },
      ],
      areasTitle: "Local SEO across the Tennessee Valley",
      areas: AREAS_EN,
      faqTitle: "Frequently asked questions",
      faqs: [
        {
          q: "How long until I see SEO results?",
          a: "Technical wins and Google Business Profile improvements often show inside 30–60 days. Sustainable ranking for competitive keywords typically compounds over 3–6 months of consistent work.",
        },
        {
          q: "Do you guarantee first-page rankings?",
          a: "No credible SEO agency guarantees rankings — Google's algorithm changes weekly. We guarantee the process, the transparency of monthly reporting, and the effort. If we aren't the right fit, you can cancel any month.",
        },
        {
          q: "Do I need a new website to do SEO?",
          a: "Not always. About half of the engagements we take on begin with technical fixes on the existing site. If the current site can't be fixed cost-effectively, we'll say so and quote a redesign separately.",
        },
        {
          q: "Do you work with Google Business Profile?",
          a: "Yes — GBP is often the highest-ROI local SEO lever. We handle setup, category selection, posts, Q&A, and photo strategy. We do not, and will not, buy fake reviews.",
        },
        {
          q: "How is progress reported?",
          a: "You receive a plain-English monthly report with ranking movement, traffic, top landing pages, calls tracked (if we install call tracking), and the concrete tasks planned for the next month.",
        },
      ],
      ctaTitle: "Ready to be found by Huntsville customers?",
      ctaBody:
        "Book a free 30-minute SEO conversation. Call +1 (256) 288-8270 or email info@yosimediastudio.com.",
      serviceType: "Search Engine Optimization",
      priceHint: "Month-to-month retainers scoped after the initial audit.",
    },
    es: {
      slug: "seo-huntsville",
      title: "Servicios de SEO en Huntsville, AL | Expertos en Búsqueda Local",
      description:
        "SEO para negocios de Huntsville: auditoría técnica, optimización on-page, Google Business Profile y contenido local. Trabajo real, reportes claros, sin contratos amarrados.",
      h1: "Servicios de SEO en Huntsville, Alabama",
      intro:
        "Ayudamos a los negocios de Huntsville a ganar las búsquedas que realmente traen clientes. Nuestro SEO parte de una auditoría técnica, aplica correcciones on-page claras y despliega un plan de contenido alineado con lo que la gente busca en el condado de Madison.",
      whyLocalTitle: "Por qué el SEO local en Huntsville es distinto",
      bullets: [
        "El comportamiento de búsqueda aquí depende mucho del map-pack — Google Business Profile pesa.",
        "Las reseñas y fotos de clientes reales de Huntsville tienen un impacto enorme.",
        "Las páginas locales deben hablar de barrios reales, no de plantillas genéricas.",
        "La fuerza laboral tecnológica y de defensa dispara búsquedas long-tail de alta intención.",
        "Los buscadores bilingües importan — producimos contenido en español cuando corresponde.",
      ],
      whatIncludedTitle: "Qué incluye nuestro servicio de SEO",
      list: [
        "Auditoría técnica con checklist de 200 puntos (Core Web Vitals, indexación, schema).",
        "Investigación de palabras clave basada en la demanda real de búsqueda en Huntsville.",
        "Optimización on-page de cada página comercial existente.",
        "Configuración o limpieza de Google Business Profile (categorías, publicaciones, Q&A).",
        "Plan de contenido local: páginas por barrio, páginas de servicio, cadencia de blog.",
        "Reporte mensual legible — sin métricas de vanidad.",
      ],
      processTitle: "Nuestro flujo de trabajo de SEO",
      steps: [
        { t: "Semanas 1–2 — Auditoría", d: "Análisis técnico, de contenido y de enlaces; mapeo de competidores." },
        { t: "Semana 3 — Estrategia", d: "Hoja de ruta priorizada con esfuerzo e impacto esperado." },
        { t: "Semanas 4+ — Ejecución", d: "Correcciones técnicas primero; contenido y GBP en paralelo." },
        { t: "Mensual — Reporte", d: "Rankings, tráfico, conversiones y prioridades del mes siguiente." },
      ],
      areasTitle: "SEO local en todo el Valle del Tennessee",
      areas: AREAS_ES,
      faqTitle: "Preguntas frecuentes",
      faqs: [
        {
          q: "¿En cuánto tiempo veré resultados de SEO?",
          a: "Los avances técnicos y las mejoras en Google Business Profile suelen notarse en 30–60 días. El posicionamiento sostenible en palabras clave competitivas normalmente se consolida en 3–6 meses de trabajo constante.",
        },
        {
          q: "¿Garantizan la primera página de Google?",
          a: "Ninguna agencia seria garantiza rankings — el algoritmo de Google cambia cada semana. Garantizamos el proceso, la transparencia del reporte mensual y el esfuerzo. Si no somos el fit correcto, puede cancelar cualquier mes.",
        },
        {
          q: "¿Necesito un sitio nuevo para hacer SEO?",
          a: "No siempre. Aproximadamente la mitad de los proyectos que tomamos empiezan con correcciones técnicas sobre el sitio existente. Si el sitio actual no se puede corregir de forma rentable, se lo diremos y cotizaremos un rediseño aparte.",
        },
        {
          q: "¿Trabajan con Google Business Profile?",
          a: "Sí — GBP suele ser la palanca de mayor ROI en SEO local. Nos encargamos de la configuración, categorías, publicaciones, Q&A y estrategia de fotos. No compramos ni compraremos reseñas falsas.",
        },
        {
          q: "¿Cómo se reporta el progreso?",
          a: "Recibe un reporte mensual en lenguaje claro con el movimiento de rankings, tráfico, principales páginas de aterrizaje, llamadas (si instalamos tracking) y las tareas concretas del siguiente mes.",
        },
      ],
      ctaTitle: "¿Listo para que los clientes de Huntsville lo encuentren?",
      ctaBody:
        "Agende una conversación de SEO gratuita de 30 minutos. Llame al +1 (256) 288-8270 o escriba a info@yosimediastudio.com.",
      serviceType: "Search Engine Optimization",
      priceHint: "Retención mes a mes, alcance definido tras la auditoría inicial.",
    },
  },

  "digital-marketing": {
    en: {
      slug: "digital-marketing-huntsville",
      title: "Digital Marketing in Huntsville, AL | Paid Ads & Funnels",
      description:
        "Google Ads, Meta ads, landing pages, and marketing funnels for Huntsville businesses. Transparent reporting, no long-term lock-in, real strategy.",
      h1: "Digital Marketing in Huntsville, Alabama",
      intro:
        "We plan and run paid marketing programs for Huntsville businesses who want measurable results — Google Search, Meta, and landing pages that turn clicks into calls and appointments. Every campaign has an owner, a hypothesis, and reporting you can actually read.",
      whyLocalTitle: "Why local paid marketing needs a local strategy",
      bullets: [
        "Bidding on 'Huntsville' generic keywords burns budget fast — geo layering matters.",
        "Meta audiences perform differently near Redstone Arsenal vs. downtown vs. Madison.",
        "Landing pages that mention real neighborhoods convert better than generic pages.",
        "Call tracking is essential: many local conversions never touch a form.",
        "We publish creative you can be proud of — no bait-and-switch imagery.",
      ],
      whatIncludedTitle: "What's inside a marketing engagement",
      list: [
        "Account audit and competitor benchmarking (if you already run ads).",
        "Funnel design: creative, landing page, offer, tracking.",
        "Google Ads and Meta Ads campaign build with proper conversion actions.",
        "Landing pages with real Huntsville imagery and location signals.",
        "Call tracking integration so every phone lead is attributed correctly.",
        "Weekly optimisation and a monthly performance review call.",
      ],
      processTitle: "The launch plan",
      steps: [
        { t: "Week 1 — Strategy", d: "Audit, offer refinement, tracking plan agreed." },
        { t: "Week 2 — Build", d: "Landing pages, creatives, conversion tracking wired up." },
        { t: "Week 3 — Launch", d: "Campaigns go live in learning mode; daily monitoring." },
        { t: "Week 4+ — Optimise", d: "Weekly bid, audience and creative refinement." },
      ],
      areasTitle: "Running campaigns across North Alabama",
      areas: AREAS_EN,
      faqTitle: "Frequently asked questions",
      faqs: [
        {
          q: "What ad budget do I need to start?",
          a: "For meaningful learning we typically recommend a monthly ad budget in a mid three-figure to low four-figure range, on top of our management fee. We'll be honest during the discovery call if we think your budget is too low to compete in your category.",
        },
        {
          q: "Do you require long-term contracts?",
          a: "No. We work month-to-month. Our expectation is that after the first 60 days you should be able to see real progress and choose to continue.",
        },
        {
          q: "Do you run both Google and Meta?",
          a: "Yes. We start with the channel that best matches your customer's buying moment. For local services that's usually Google Search plus Google Business Profile; for retail and lifestyle brands Meta often leads.",
        },
        {
          q: "Can you build the landing pages too?",
          a: "Yes — landing pages are part of the engagement and we build them fast on our own stack so we can iterate weekly based on real ad data.",
        },
        {
          q: "How is my data protected?",
          a: "All ad accounts stay under your ownership. We work inside your accounts as managers — you can revoke access at any time and keep every campaign.",
        },
      ],
      ctaTitle: "Ready to run marketing that actually reports back?",
      ctaBody:
        "Call +1 (256) 288-8270 or email info@yosimediastudio.com to book a 30-minute strategy call — no pitch deck, just a working session.",
      serviceType: "Digital Marketing",
      priceHint: "Month-to-month management fee, quoted after strategy call.",
    },
    es: {
      slug: "marketing-digital-huntsville",
      title: "Marketing Digital en Huntsville, AL | Anuncios y Embudos",
      description:
        "Google Ads, anuncios en Meta, landing pages y embudos de marketing para negocios de Huntsville. Reportes transparentes, sin contratos amarrados, estrategia real.",
      h1: "Marketing Digital en Huntsville, Alabama",
      intro:
        "Planificamos y ejecutamos programas de marketing pagado para negocios de Huntsville que buscan resultados medibles: Google Search, Meta y landing pages que convierten clics en llamadas y citas. Cada campaña tiene un responsable, una hipótesis y un reporte que sí se entiende.",
      whyLocalTitle: "Por qué el marketing pagado local necesita estrategia local",
      bullets: [
        "Pujar por keywords genéricas de 'Huntsville' quema presupuesto rápido — la geo-segmentación importa.",
        "Las audiencias de Meta rinden distinto cerca de Redstone Arsenal, downtown o Madison.",
        "Las landing pages que mencionan barrios reales convierten mejor que las genéricas.",
        "El tracking de llamadas es esencial: muchas conversiones locales nunca tocan un formulario.",
        "Publicamos creativos de los que se puede estar orgulloso — sin imágenes engañosas.",
      ],
      whatIncludedTitle: "Qué incluye un servicio de marketing",
      list: [
        "Auditoría de cuenta y benchmarking de competencia (si ya corre anuncios).",
        "Diseño de embudo: creativo, landing, oferta, tracking.",
        "Construcción de campañas en Google Ads y Meta Ads con conversiones bien configuradas.",
        "Landing pages con imágenes reales de Huntsville y señales de ubicación.",
        "Tracking de llamadas para atribuir correctamente cada lead telefónico.",
        "Optimización semanal y reunión mensual de desempeño.",
      ],
      processTitle: "Plan de lanzamiento",
      steps: [
        { t: "Semana 1 — Estrategia", d: "Auditoría, refinamiento de oferta, plan de tracking acordado." },
        { t: "Semana 2 — Construcción", d: "Landings, creativos y conversiones armadas." },
        { t: "Semana 3 — Lanzamiento", d: "Campañas activas en modo aprendizaje; monitoreo diario." },
        { t: "Semana 4+ — Optimización", d: "Ajustes semanales de puja, audiencia y creativo." },
      ],
      areasTitle: "Campañas en el Norte de Alabama",
      areas: AREAS_ES,
      faqTitle: "Preguntas frecuentes",
      faqs: [
        {
          q: "¿Qué presupuesto de anuncios necesito para empezar?",
          a: "Para un aprendizaje significativo solemos recomendar un presupuesto mensual de anuncios de tres a cuatro cifras bajas, además de nuestro fee de gestión. Si su presupuesto es muy bajo para su categoría, se lo diremos con claridad en el discovery.",
        },
        {
          q: "¿Requieren contratos largos?",
          a: "No. Trabajamos mes a mes. Esperamos que a los 60 días vea progreso real y decida continuar.",
        },
        {
          q: "¿Manejan tanto Google como Meta?",
          a: "Sí. Empezamos por el canal que mejor calce con el momento de compra de su cliente. Para servicios locales suele ser Google Search + Google Business Profile; para retail y estilo de vida, Meta suele liderar.",
        },
        {
          q: "¿Pueden armar también las landing pages?",
          a: "Sí — las landing pages son parte del servicio y las construimos rápido en nuestro propio stack para iterar semanalmente con los datos reales de la campaña.",
        },
        {
          q: "¿Cómo se protege mi información?",
          a: "Todas las cuentas de anuncios quedan a su nombre. Trabajamos como gestores dentro de sus cuentas — puede quitarnos el acceso en cualquier momento y conservar todas las campañas.",
        },
      ],
      ctaTitle: "¿Listo para un marketing que sí reporta?",
      ctaBody:
        "Llame al +1 (256) 288-8270 o escriba a info@yosimediastudio.com para agendar 30 minutos de estrategia — sin presentaciones, solo trabajo.",
      serviceType: "Digital Marketing",
      priceHint: "Fee de gestión mes a mes, cotizado tras la llamada de estrategia.",
    },
  },

  "ai-automation": {
    en: {
      slug: "ai-automation-huntsville",
      title: "AI Automation for Huntsville Businesses | Yosi Media Studio",
      description:
        "Custom AI automation for Huntsville businesses — lead qualification bots, quote generation, customer support agents and workflow automations, done right.",
      h1: "AI Automation in Huntsville, Alabama",
      intro:
        "We help Huntsville businesses put AI to work on the boring parts of the day — qualifying leads, answering questions, drafting quotes, and moving data between systems. We ship focused automations that pay for themselves, not shiny demos.",
      whyLocalTitle: "Where AI is actually paying off for local businesses",
      bullets: [
        "24/7 lead qualification for service businesses whose leads arrive after hours.",
        "Multi-language customer support (English + Spanish) using the same knowledge base.",
        "Automated quote drafting that a human still reviews before it goes out.",
        "Meeting notes, follow-up emails, and CRM updates without extra headcount.",
        "Every automation ships with a kill switch and a human-review path.",
      ],
      whatIncludedTitle: "What's inside an AI engagement",
      list: [
        "Workflow discovery: which handful of tasks are worth automating first.",
        "Knowledge base ingestion (your docs, past quotes, past support tickets).",
        "Pilot in a supervised environment — nothing goes to customers on day one.",
        "Integration with your CRM, email, WhatsApp or website.",
        "Guardrails: retrieval, human-in-the-loop, fallbacks and observability.",
        "Training so your team can run and adjust the automations themselves.",
      ],
      processTitle: "How we deploy AI safely",
      steps: [
        { t: "Week 1 — Discovery", d: "Map candidate workflows; select the 1–2 with clearest ROI." },
        { t: "Week 2 — Training", d: "Ingest knowledge, define tone, set guardrails." },
        { t: "Week 3 — Pilot", d: "Supervised runs with real messages — you approve every reply." },
        { t: "Week 4 — Launch", d: "Turn on autonomy gradually; monitor and iterate weekly." },
      ],
      areasTitle: "Serving Huntsville and the Tennessee Valley",
      areas: AREAS_EN,
      faqTitle: "Frequently asked questions",
      faqs: [
        {
          q: "Which AI model do you use?",
          a: "It depends on the task. We benchmark GPT-class, Claude and Gemini for each workflow and pick whichever is strongest and most cost-effective. We never lock you into a single vendor.",
        },
        {
          q: "Is my data used to train public models?",
          a: "No. We use API endpoints with data-retention controls enabled, and we scope conversation data per client. Your knowledge base is never mixed with anyone else's.",
        },
        {
          q: "Can the AI escalate to a human?",
          a: "Yes — human-in-the-loop is a core part of every automation we ship. The AI hands off cleanly to a real teammate with full context when it's out of its depth.",
        },
        {
          q: "What if the AI says something wrong?",
          a: "We design every deployment with retrieval, guardrails and structured logging so wrong answers are rare, catchable and fixable. We also set a hard scope — the AI won't answer topics outside its permission.",
        },
        {
          q: "How is pricing structured?",
          a: "There's a one-time build cost per workflow and a small monthly fee that covers hosting, model usage and monitoring. We'll quote both fixed after the discovery session.",
        },
      ],
      ctaTitle: "Ready to see where AI can help your business?",
      ctaBody:
        "Book a 30-minute AI opportunity call. Call +1 (256) 288-8270 or email info@yosimediastudio.com.",
      serviceType: "AI Automation",
      priceHint: "Fixed build + small monthly hosting fee, quoted per workflow.",
    },
    es: {
      slug: "automatizacion-ia-huntsville",
      title: "Automatización con IA en Huntsville, AL | Yosi Media Studio",
      description:
        "Automatización con IA para negocios de Huntsville: bots de calificación de leads, generación de cotizaciones, agentes de soporte y automatizaciones de flujos.",
      h1: "Automatización con Inteligencia Artificial en Huntsville, Alabama",
      intro:
        "Ayudamos a los negocios de Huntsville a poner la IA a trabajar en las tareas aburridas del día — calificar leads, responder preguntas, redactar cotizaciones y mover datos entre sistemas. Entregamos automatizaciones enfocadas que sí se pagan solas, no demos vistosas.",
      whyLocalTitle: "Dónde la IA está pagando de verdad en negocios locales",
      bullets: [
        "Calificación de leads 24/7 para negocios de servicio cuyos leads llegan fuera de horario.",
        "Soporte al cliente bilingüe (inglés y español) usando la misma base de conocimiento.",
        "Borrador automático de cotizaciones que un humano revisa antes de enviar.",
        "Notas de reunión, emails de seguimiento y actualizaciones de CRM sin sumar personal.",
        "Cada automatización incluye botón de apagado y ruta de revisión humana.",
      ],
      whatIncludedTitle: "Qué incluye un proyecto de IA",
      list: [
        "Discovery de flujos: qué pocas tareas conviene automatizar primero.",
        "Ingesta de la base de conocimiento (documentos, cotizaciones previas, tickets pasados).",
        "Piloto en entorno supervisado — nada llega al cliente el primer día.",
        "Integración con su CRM, email, WhatsApp o sitio web.",
        "Guardrails: recuperación, humano-en-el-loop, fallbacks y observabilidad.",
        "Capacitación para que su equipo pueda operar y ajustar las automatizaciones.",
      ],
      processTitle: "Cómo desplegamos IA con seguridad",
      steps: [
        { t: "Semana 1 — Discovery", d: "Mapeo de flujos candidatos; se eligen 1–2 con ROI más claro." },
        { t: "Semana 2 — Entrenamiento", d: "Ingesta de conocimiento, definición de tono y guardrails." },
        { t: "Semana 3 — Piloto", d: "Corridas supervisadas con mensajes reales — usted aprueba cada respuesta." },
        { t: "Semana 4 — Lanzamiento", d: "Autonomía gradual; monitoreo e iteración semanal." },
      ],
      areasTitle: "Atendemos Huntsville y el Valle del Tennessee",
      areas: AREAS_ES,
      faqTitle: "Preguntas frecuentes",
      faqs: [
        {
          q: "¿Qué modelo de IA usan?",
          a: "Depende de la tarea. Hacemos benchmarks con GPT, Claude y Gemini para cada flujo y elegimos el más fuerte y costo-eficiente. Nunca lo amarramos a un solo proveedor.",
        },
        {
          q: "¿Mis datos se usan para entrenar modelos públicos?",
          a: "No. Usamos endpoints de API con retención de datos apagada y aislamos la conversación por cliente. Su base de conocimiento nunca se mezcla con la de otros.",
        },
        {
          q: "¿La IA puede escalar a un humano?",
          a: "Sí — el humano-en-el-loop es parte central de cada automatización. La IA entrega el caso limpio a un miembro real del equipo con todo el contexto cuando algo se sale de su alcance.",
        },
        {
          q: "¿Y si la IA dice algo incorrecto?",
          a: "Diseñamos cada despliegue con recuperación, guardrails y logs estructurados para que las respuestas incorrectas sean raras, detectables y corregibles. Además fijamos un alcance duro — la IA no responde temas fuera de su permiso.",
        },
        {
          q: "¿Cómo se cobra?",
          a: "Un costo único de construcción por flujo y un fee mensual pequeño para hosting, uso de modelo y monitoreo. Ambos se cotizan fijos tras la sesión de discovery.",
        },
      ],
      ctaTitle: "¿Listo para ver dónde la IA puede ayudar a su negocio?",
      ctaBody:
        "Agende 30 minutos para explorar oportunidades. Llame al +1 (256) 288-8270 o escriba a info@yosimediastudio.com.",
      serviceType: "AI Automation",
      priceHint: "Costo fijo de construcción + fee mensual pequeño, cotizado por flujo.",
    },
  },

  "printing-branding": {
    en: {
      slug: "printing-branding-huntsville",
      title: "Printing & Branding in Huntsville, AL | Yosi Media Studio",
      description:
        "Business cards, uniforms, signage, NFC smart cards, and full brand identity for Huntsville businesses. Local delivery, real production partners.",
      h1: "Printing & Branding in Huntsville, Alabama",
      intro:
        "From your logo to the shirts your team wears at events, we handle the physical side of your brand — designed in-house, printed with trusted local and national partners, and delivered on time. Every printed asset is designed to match your digital presence exactly.",
      whyLocalTitle: "Why local printing still matters",
      bullets: [
        "Faster turnaround than mail-order chains for time-sensitive events.",
        "Local proofing — we walk you through samples in person when it counts.",
        "Cohesive brand: what you print matches what you publish online.",
        "Sustainable stocks available if that matters to your brand.",
        "NFC smart cards let a Huntsville trade show contact scan you instantly.",
      ],
      whatIncludedTitle: "What we design and produce",
      list: [
        "Logo, color system, and full brand identity guidelines.",
        "Business cards (standard and premium finishes) + NFC smart cards.",
        "Company uniforms and event apparel — screen print, embroidery, DTG.",
        "Signage: windows, storefront, event backdrops, vehicle wraps.",
        "Promotional swag: mugs, notebooks, tote bags, event kits.",
        "Print-ready flyers, brochures, presentation folders, invoices.",
      ],
      processTitle: "From concept to delivered box",
      steps: [
        { t: "Discovery", d: "Brand review, quantities, deadlines and delivery point agreed." },
        { t: "Design", d: "In-house design; two rounds of revisions included." },
        { t: "Proof", d: "Digital and, when it matters, a physical proof before we press go." },
        { t: "Produce & Deliver", d: "Production with trusted partners; delivery to your Huntsville location." },
      ],
      areasTitle: "Delivering across North Alabama",
      areas: AREAS_EN,
      faqTitle: "Frequently asked questions",
      faqs: [
        {
          q: "How fast can you turn around business cards?",
          a: "For a design we already hold, we can usually deliver premium printed cards inside 5–7 business days in Huntsville. Rush turnaround is available on a case-by-case basis.",
        },
        {
          q: "Do you print in-house or use partners?",
          a: "We design in-house and produce with vetted local and national print partners depending on the finish and quantity. You get the quality of a specialist press with the accountability of one point of contact.",
        },
        {
          q: "Can you match colors from my existing brand?",
          a: "Yes. If you have brand guidelines, we honor them exactly. If you don't, we'll build a small brand-color reference (Pantone + CMYK + digital) so every print job stays consistent.",
        },
        {
          q: "What are NFC smart cards?",
          a: "A premium business card with an embedded chip. Someone taps their phone on the card and your contact details, website or Google review link open instantly — no app required. Great for Huntsville trade shows and event networking.",
        },
        {
          q: "Do you handle uniforms and event apparel?",
          a: "Yes — screen printing, embroidery and DTG. We can dress a team of five for an event or ramp to a couple of hundred pieces for a company rollout.",
        },
      ],
      ctaTitle: "Ready to make your brand physical?",
      ctaBody:
        "Send us the deadline and quantities and we'll come back with a fixed quote. Call +1 (256) 288-8270 or email info@yosimediastudio.com.",
      serviceType: "Print Design and Production",
      priceHint: "Fixed quotes per job — no minimums for existing clients.",
    },
    es: {
      slug: "impresion-branding-huntsville",
      title: "Impresión y Branding en Huntsville, AL | Yosi Media Studio",
      description:
        "Tarjetas de presentación, uniformes, señalética, tarjetas NFC e identidad de marca completa para negocios de Huntsville. Entrega local, socios de producción reales.",
      h1: "Impresión y Branding en Huntsville, Alabama",
      intro:
        "Desde su logo hasta las camisas que su equipo usa en eventos, cubrimos la parte física de su marca — diseñada en casa, impresa con socios locales y nacionales confiables, y entregada a tiempo. Cada pieza impresa está diseñada para calzar exactamente con su presencia digital.",
      whyLocalTitle: "Por qué la impresión local aún importa",
      bullets: [
        "Tiempos más rápidos que cadenas por correo para eventos con fecha.",
        "Pruebas locales — revisamos muestras en persona cuando conviene.",
        "Marca coherente: lo impreso calza con lo que publica en línea.",
        "Papeles sostenibles disponibles si su marca los prioriza.",
        "Las tarjetas NFC permiten a un contacto de feria en Huntsville escanearlo al instante.",
      ],
      whatIncludedTitle: "Qué diseñamos y producimos",
      list: [
        "Logo, sistema de color y manual completo de identidad.",
        "Tarjetas de presentación (estándar y premium) + tarjetas NFC.",
        "Uniformes corporativos y ropa de evento — serigrafía, bordado, DTG.",
        "Señalética: ventanas, fachadas, backdrops de evento, rotulación vehicular.",
        "Merchandising: tazas, libretas, tote bags, kits de evento.",
        "Volantes, brochures, carpetas de presentación y facturas listos para imprenta.",
      ],
      processTitle: "Del concepto a la caja entregada",
      steps: [
        { t: "Discovery", d: "Revisión de marca, cantidades, plazos y punto de entrega acordados." },
        { t: "Diseño", d: "Diseño interno; dos rondas de revisión incluidas." },
        { t: "Prueba", d: "Prueba digital y, cuando corresponde, prueba física antes de imprimir." },
        { t: "Producción y entrega", d: "Producción con socios confiables; entrega en su punto de Huntsville." },
      ],
      areasTitle: "Entregamos en todo el Norte de Alabama",
      areas: AREAS_ES,
      faqTitle: "Preguntas frecuentes",
      faqs: [
        {
          q: "¿Qué tan rápido pueden entregar tarjetas de presentación?",
          a: "Con un diseño ya aprobado, solemos entregar tarjetas premium en 5–7 días hábiles en Huntsville. La entrega express se evalúa caso por caso.",
        },
        {
          q: "¿Imprimen internamente o con socios?",
          a: "Diseñamos internamente y producimos con socios locales y nacionales seleccionados según el acabado y la cantidad. Recibe la calidad de una prensa especialista con un solo punto de contacto.",
        },
        {
          q: "¿Pueden respetar los colores de mi marca actual?",
          a: "Sí. Si tiene un manual de marca, lo respetamos al pie de la letra. Si no lo tiene, construimos una referencia mínima (Pantone + CMYK + digital) para que cada trabajo quede consistente.",
        },
        {
          q: "¿Qué son las tarjetas NFC?",
          a: "Una tarjeta premium con un chip embebido. Al tocarla con el celular se abren instantáneamente sus datos, sitio web o enlace de reseñas de Google — sin app. Ideal para ferias y networking en Huntsville.",
        },
        {
          q: "¿Hacen uniformes y ropa de evento?",
          a: "Sí — serigrafía, bordado y DTG. Podemos vestir un equipo de cinco personas para un evento o escalar a un par de cientos de piezas para un rollout corporativo.",
        },
      ],
      ctaTitle: "¿Listo para llevar su marca al plano físico?",
      ctaBody:
        "Envíenos el plazo y las cantidades y le devolvemos una cotización fija. Llame al +1 (256) 288-8270 o escriba a info@yosimediastudio.com.",
      serviceType: "Print Design and Production",
      priceHint: "Cotizaciones fijas por trabajo — sin mínimos para clientes existentes.",
    },
  },
};

/** Ordered list of the five landing-page service keys — used by sitemap + index page. */
export const LOCAL_SEO_KEYS = ["web-design", "seo", "digital-marketing", "ai-automation", "printing-branding"];
